import { useMemo, PropsWithChildren, useReducer, useCallback } from "react";
import { useGenericContext } from "../../hooks";
import { IQueryParamsContext, QueryParamsContext, DefaultParams, QueryParamsReducer } from ".";
import { Entity, PageParams, ParamsApi, QueryParams, SortParams } from "../../models";

export const QueryParamsProvider = (props: PropsWithChildren) => {
    const [params, dispatchParams] = useReducer(QueryParamsReducer, DefaultParams);

    const getQueryParams = useCallback((entity: Entity): QueryParams => params[entity].queryParams, [params]);

    const getSortParams = useCallback((entity: Entity): SortParams => params[entity].sortParams, [params]);

    const getPageParams = useCallback((entity: Entity): PageParams => params[entity].pageParams, [params]);

    /**
     * Clears the query params for the given entity
     */
    const clearQueryParams = useCallback((entity: Entity) => {
        dispatchParams({ type: "clear", entity, paramType: "query" });
    }, []);

    const clearSortParams = useCallback((entity: Entity) => {
        dispatchParams({ type: "clear", entity, paramType: "sort" });
    }, []);

    const clearPageParams = useCallback((entity: Entity) => {
        dispatchParams({ type: "clear", entity, paramType: "page" });
    }, []);

    /**
     * Updates a single key/value pair in the query params
     */
    const updateQueryParams = useCallback((entity: Entity, key: string, value: string) => {
        dispatchParams({ type: "update", entity, key, value, paramType: "query" });
    }, []);

    const updateSortParams = useCallback((entity: Entity, key: string, value: string) => {
        dispatchParams({ type: "update", entity, key, value, paramType: "sort" });
    }, []);

    const updatePageParams = useCallback((entity: Entity, key: string, value: number) => {
        dispatchParams({ type: "update", entity, key, value, paramType: "page" });
    }, []);

    /**
     * Replaces the entire query params object
     */
    const setQueryParams = useCallback((entity: Entity, queryParams: QueryParams) => {
        dispatchParams({ type: "replace", entity, payload: queryParams, paramType: "query" });
    }, []);

    const setSortParams = useCallback((entity: Entity, sortParams: SortParams) => {
        dispatchParams({ type: "replace", entity, payload: sortParams, paramType: "sort" });
    }, []);

    const setPageParams = useCallback((entity: Entity, pageParams: PageParams) => {
        dispatchParams({ type: "replace", entity, payload: pageParams, paramType: "page" });
    }, []);

    const getParamsApi = useCallback(
        (entity: Entity): ParamsApi => ({
            pageParams: getPageParams(entity),
            sortParams: getSortParams(entity),
            queryParams: getQueryParams(entity),
            updatePageParams: (key: string, value: number) => updatePageParams(entity, key, value),
            setPageParams: (pageParams: PageParams) => setPageParams(entity, pageParams),
            updateSortParams: (key: string, value: string) => updateSortParams(entity, key, value),
            setSortParams: (sortParams: SortParams) => setSortParams(entity, sortParams),
            updateQueryParams: (key: string, value: string) => updateQueryParams(entity, key, value),
            setQueryParams: (queryParams: QueryParams) => setQueryParams(entity, queryParams),
            clearQueryParams: () => clearQueryParams(entity),
            clearSortParams: () => clearSortParams(entity),
            clearPageParams: () => clearPageParams(entity),
        }),
        [
            getPageParams,
            getSortParams,
            getQueryParams,
            updatePageParams,
            setPageParams,
            updateSortParams,
            setSortParams,
            updateQueryParams,
            setQueryParams,
            clearQueryParams,
            clearSortParams,
            clearPageParams,
        ]
    );

    const value = useMemo<IQueryParamsContext>(
        () => ({
            getParamsApi,
        }),
        [getParamsApi]
    );

    return <QueryParamsContext.Provider value={value} {...props} />;
};
